/**
 * @fileoverview FAQ translations for all supported locales.
 * Keyed by BCP-47 language code (matching the lang/ folder names).
 * The 'en' entry is the source of truth — other entries must mirror its structure.
 */

export interface FaqEntry {
    question: string;
    answer: string;
}

import type { SupportedLang } from './lang.js';
export type { SupportedLang };

export const faqTranslations: Record<SupportedLang, { heading: string; faqs: FaqEntry[] }> = {

    en: {
        heading: 'Frequently Asked Questions',
        faqs: [
            {
                question: 'For what age is this game appropriate?',
                answer: 'Match & Learn is designed for toddlers aged 2 to 3 years old. The large drag targets and forgiving mechanics make it accessible for very young children.',
            },
            {
                question: 'Does it work on old iPads and tablets?',
                answer: 'Yes! Match & Learn is specifically optimized to run smoothly on older iPads and Android tablets. No downloads are required — it runs directly in the browser.',
            },
            {
                question: 'Is it free to play?',
                answer: 'Yes, Match & Learn is completely free to play. It includes matching games with animals, fruits, shapes, numbers, letters, and more.',
            },
            {
                question: 'What themes and activities are available?',
                answer: 'The game includes many educational themes: animals, fruits, shapes, numbers, letters, vehicles, food, and more. There are also bedtime stories for toddlers built in.',
            },
        ],
    },

    de: {
        heading: 'Häufig gestellte Fragen',
        faqs: [
            {
                question: 'Für welches Alter ist dieses Spiel geeignet?',
                answer: 'Match & Learn ist für Kleinkinder von 2 bis 3 Jahren konzipiert. Die großen Zielbereiche und die nachsichtigen Spielmechaniken machen es für sehr kleine Kinder zugänglich.',
            },
            {
                question: 'Funktioniert es auf alten iPads und Tablets?',
                answer: 'Ja! Match & Learn ist speziell optimiert, um auf älteren iPads und Android-Tablets reibungslos zu laufen. Kein Download nötig – es läuft direkt im Browser.',
            },
            {
                question: 'Ist es kostenlos spielbar?',
                answer: 'Ja, Match & Learn ist vollständig kostenlos. Es enthält Zuordnungsspiele mit Tieren, Früchten, Formen, Zahlen, Buchstaben und mehr.',
            },
            {
                question: 'Welche Themen und Aktivitäten sind verfügbar?',
                answer: 'Das Spiel umfasst viele pädagogische Themen: Tiere, Früchte, Formen, Zahlen, Buchstaben, Fahrzeuge, Essen und mehr. Auch Gutenachtgeschichten für Kleinkinder sind enthalten.',
            },
        ],
    },

    es: {
        heading: 'Preguntas Frecuentes',
        faqs: [
            {
                question: '¿Para qué edad es este juego?',
                answer: 'Match & Learn está diseñado para niños de 2 a 3 años. Los grandes objetivos y la mecánica tolerante lo hacen accesible para los más pequeños.',
            },
            {
                question: '¿Funciona en iPads y tablets antiguos?',
                answer: '¡Sí! Match & Learn está optimizado para funcionar en iPads y tablets Android más antiguos. No se requieren descargas: funciona directamente en el navegador.',
            },
            {
                question: '¿Es gratis?',
                answer: 'Sí, Match & Learn es completamente gratuito. Incluye juegos de emparejamiento con animales, frutas, formas, números, letras y más.',
            },
            {
                question: '¿Qué temas y actividades hay disponibles?',
                answer: 'El juego incluye muchos temas educativos: animales, frutas, formas, números, letras, vehículos, comida y más. También hay cuentos para dormir para niños pequeños.',
            },
        ],
    },

    fr: {
        heading: 'Foire Aux Questions',
        faqs: [
            {
                question: 'Pour quel âge est ce jeu ?',
                answer: 'Match & Learn est conçu pour les enfants de 2 à 3 ans. Les grandes zones de dépôt et les règles indulgentes le rendent accessible aux très jeunes enfants.',
            },
            {
                question: 'Fonctionne-t-il sur les vieilles iPads et tablettes ?',
                answer: 'Oui ! Match & Learn est optimisé pour fonctionner sur les iPads et tablettes Android plus anciens. Aucun téléchargement n\'est requis : il fonctionne directement dans le navigateur.',
            },
            {
                question: 'Est-il gratuit ?',
                answer: 'Oui, Match & Learn est entièrement gratuit. Il comprend des jeux d\'association avec des animaux, des fruits, des formes, des chiffres, des lettres et plus encore.',
            },
            {
                question: 'Quels thèmes et activités sont disponibles ?',
                answer: 'Le jeu comprend de nombreux thèmes éducatifs : animaux, fruits, formes, chiffres, lettres, véhicules, nourriture et plus encore. Des histoires du soir pour tout-petits sont également incluses.',
            },
        ],
    },

    pt: {
        heading: 'Perguntas Frequentes',
        faqs: [
            {
                question: 'Para qual idade este jogo é adequado?',
                answer: 'Match & Learn é projetado para crianças de 2 a 3 anos. Os grandes alvos e a mecânica tolerante o tornam acessível para crianças muito pequenas.',
            },
            {
                question: 'Funciona em iPads e tablets antigos?',
                answer: 'Sim! Match & Learn é otimizado para funcionar em iPads e tablets Android mais antigos. Nenhum download é necessário — funciona diretamente no navegador.',
            },
            {
                question: 'É grátis?',
                answer: 'Sim, Match & Learn é completamente gratuito. Inclui jogos de combinação com animais, frutas, formas, números, letras e muito mais.',
            },
            {
                question: 'Quais temas e atividades estão disponíveis?',
                answer: 'O jogo inclui muitos temas educativos: animais, frutas, formas, números, letras, veículos, comida e muito mais. Também há histórias para dormir para crianças pequenas incluídas.',
            },
        ],
    },

    bs: {
        heading: 'Često Postavljana Pitanja',
        faqs: [
            {
                question: 'Za koji uzrast je ova igra prikladna?',
                answer: 'Match & Learn je dizajniran za malu djecu uzrasta od 2 do 3 godine. Velike mete za povlačenje i opraštajuća mehanika čine je dostupnom za vrlo malu djecu.',
            },
            {
                question: 'Da li radi na starim iPadima i tabletima?',
                answer: 'Da! Match & Learn je posebno optimizovan da radi glatko na starijim iPadima i Android tabletima. Nije potrebno preuzimanje — radi direktno u pregledniku.',
            },
            {
                question: 'Da li je besplatna za igranje?',
                answer: 'Da, Match & Learn je potpuno besplatan za igranje. Uključuje igre spajanja sa životinjama, voćem, oblicima, brojevima, slovima i još mnogo toga.',
            },
            {
                question: 'Koje teme i aktivnosti su dostupne?',
                answer: 'Igra uključuje mnoge obrazovne teme: životinje, voće, oblike, brojeve, slova, vozila, hranu i još mnogo toga. Ugrađene su i priče za laku noć za malu djecu.',
            },
        ],
    },
};

